import React from "react";
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import Stack from "@mui/joy/Stack";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import FormHelperText from "@mui/joy/FormHelperText";
import Textarea from "@mui/joy/Textarea";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { getPages } from "../../../common/pages/pages";
import { Game } from "../../../models/games";
import { gameName as knownGameName, toNum } from "../../bids/analytics/format";

/** USD с копейками, «—» если нет значения */
export function formatUsd(value: number | string | null | undefined, digits = 2): string {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    return `$${toNum(value).toLocaleString("ru-RU", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

/** Проценты, пришедшие как 0..100 */
export function formatPercent(value: number | string | null | undefined, digits = 2): string {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    return `${toNum(value).toLocaleString("ru-RU", { maximumFractionDigits: digits })}%`;
}


export function resolveGameName(games: Game[], gameId: number | null | undefined): string {
    if (gameId === null || gameId === undefined) {
        return "Все игры";
    }
    const game = games.find((g) => g.id === gameId);
    return game?.name || knownGameName(gameId);
}

export function Pager(props: { page: number; pageCount: number; loading?: boolean; onChange: (page: number) => void }) {
    const { page, pageCount, loading, onChange } = props;
    if (pageCount <= 1) {
        return null;
    }
    return (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ p: 1 }}>
            <IconButton variant="plain" disabled={page <= 1 || loading} onClick={() => onChange(Math.max(1, page - 1))}>
                <KeyboardArrowLeftIcon />
            </IconButton>
            {getPages(pageCount, page).map((p, i) => {
                const isDots = p === "...";
                const pageNum = isDots ? NaN : Number(p);
                return (
                    <Button
                        key={`${p}-${i}`}
                        size="sm"
                        variant={!isDots && pageNum === page ? "solid" : "plain"}
                        disabled={isDots || loading}
                        onClick={() => !isDots && onChange(pageNum)}
                    >
                        {p}
                    </Button>
                );
            })}
            <IconButton
                variant="plain"
                disabled={page >= pageCount || loading}
                onClick={() => onChange(Math.min(pageCount, page + 1))}
            >
                <KeyboardArrowRightIcon />
            </IconButton>
        </Stack>
    );
}

/**
 * Подтверждение включения/выключения с причиной — она попадает в журнал изменений поставщика
 */
export function ReasonDialog(props: {
    open: boolean;
    title: string;
    description: React.ReactNode;
    confirmLabel: string;
    confirmColor?: "primary" | "danger" | "success";
    reasonRequired?: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => Promise<void>;
}) {
    const { open, title, description, confirmLabel, confirmColor = "primary", reasonRequired, onClose, onConfirm } = props;
    const [reason, setReason] = React.useState("");
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (open) {
            setReason("");
        }
    }, [open]);

    return (
        <Modal open={open} onClose={() => !saving && onClose()}>
            <ModalDialog sx={{ minWidth: { sm: 420 } }}>
                <ModalClose disabled={saving} />
                <Typography level="h4">{title}</Typography>
                <Typography level="body-sm">{description}</Typography>
                <FormControl>
                    <FormLabel>Причина</FormLabel>
                    <Textarea minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                    <FormHelperText>
                        {reasonRequired
                            ? "Обязательно: попадёт в журнал изменений поставщика и в Slack"
                            : "Попадёт в журнал изменений поставщика"}
                    </FormHelperText>
                </FormControl>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="plain" color="neutral" disabled={saving} onClick={onClose}>
                        Отмена
                    </Button>
                    <Button
                        color={confirmColor}
                        loading={saving}
                        disabled={reasonRequired && !reason.trim()}
                        onClick={async () => {
                            setSaving(true);
                            try {
                                await onConfirm(reason.trim());
                            } finally {
                                setSaving(false);
                            }
                        }}
                    >
                        {confirmLabel}
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}

/** Число из поля ввода: пусто → undefined */
export function parseOptionalNumber(value: string): number | undefined {
    const normalized = value.replace(",", ".").trim();
    if (!normalized) {
        return undefined;
    }
    const n = Number(normalized);
    return Number.isFinite(n) ? n : undefined;
}

/** Рубли из поля ввода в копейки: пусто → undefined */
export function rublesToKopecksOptional(value: string): number | undefined {
    const n = parseOptionalNumber(value);
    return n === undefined ? undefined : Math.round(n * 100);
}

export function kopecksToRublesInput(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === "") {
        return "";
    }
    return String(toNum(value) / 100);
}
